// Cổng vào khu Quản trị NPP: chỉ tài khoản admin NPP; chờ phiên quản trị (lib/nppAdmin.ts) sẵn sàng.
// Không xin được phiên (NPP bị khoá, tài khoản bị khoá, mạng…) → báo rõ + thử lại / đăng xuất, không trắng màn.

import { useEffect, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { useAuthState } from '@/hooks/useAuthState'
import { connectNppAdmin, useNppAdmin, canWith } from '@/lib/nppAdmin'
import { logout } from '@/lib/auth'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'

export function NppGate({ children }: { children: ReactNode }) {
  const auth = useAuthState()
  const admin = useNppAdmin()
  const nav = useNavigate()

  useEffect(() => {
    if (auth.nppAdmin && admin.status === 'idle') void connectNppAdmin()
  }, [auth.nppAdmin, admin.status])

  if (!auth.nppAdmin) return <Navigate to="/" replace />
  if (admin.status === 'ready') return <>{children}</>
  if (admin.status === 'error') {
    return (
      <div className="grid min-h-dvh place-items-center bg-paper px-6">
        <div className="max-w-sm text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="size-6" aria-hidden />
          </span>
          <p className="mt-4 font-medium text-ink">Chưa mở được khu Quản trị NPP</p>
          <p className="mt-1 text-sm text-inkSoft">{admin.error}</p>
          <div className="mt-6 flex justify-center gap-2">
            <Button variant="outline" onClick={() => void connectNppAdmin({ fresh: true })}>Thử lại</Button>
            <Button
              variant="ghost"
              onClick={async () => {
                await logout()
                nav('/login', { replace: true })
              }}
            >
              Đăng xuất
            </Button>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="grid min-h-dvh place-items-center bg-paper text-muted">
      <div className="flex items-center gap-2 text-sm">
        <Spinner />
        Đang mở khu Quản trị NPP…
      </div>
    </div>
  )
}

/** Một trang trong khu NPP: không đủ quyền thì báo, không gọi API để nhận 403. */
export function NppRoute({ anyOf, children }: { anyOf: readonly string[]; children: ReactNode }) {
  const admin = useNppAdmin()
  if (!canWith(admin, anyOf)) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="font-medium text-ink">Bạn chưa được cấp quyền xem mục này</p>
      </div>
    )
  }
  return <>{children}</>
}
